import { generateId } from '@freelancer/datastore/testing';
import {
  QuotationContextTypeApi,
  QuotationStatusApi,
} from 'api-typings/quotations/quotations';
import { addDays } from 'date-fns';
import {
  QuotationAmount,
  QuotationRevision,
} from './quotation-revisions.model';

export interface GenerateQuotationRevisionOptions {
  readonly creatorId: number;
  readonly quotationVersionId?: number; // quotation version id
  readonly quotationId?: number;
  readonly contextType?: QuotationContextTypeApi;
  readonly contextId?: number;
  readonly createDate?: number;
  readonly isRequestedByRecipient?: boolean;
  readonly defaultCurrencyId?: number;
  readonly approveDate?: number;
  readonly paidAmount?: number;
  readonly fundedAmount?: number;
  readonly quotePrice?: number;
  readonly title?: string;
  readonly description?: string;
  readonly versionInvalidDate?: number;
  readonly parentVersionId?: number;
  readonly status?: QuotationStatusApi;
  readonly estimatedStartDate?: number;
  readonly estimatedEndDate?: number;
  readonly upfrontFundRequestAmount?: number;
  readonly expiryDate?: number;
  readonly cancelDate?: number;
  readonly rejectDate?: number;
}

// Returns an object of type QuotationAmount
function getQuotationAmountObject({
  currencyId,
  amount,
}: {
  readonly currencyId: number;
  readonly amount: number;
}): QuotationAmount {
  return { [currencyId]: amount };
}

export function generateQuotationRevisionObject({
  creatorId,
  quotationVersionId,
  quotationId,
  contextType = QuotationContextTypeApi.PROJECT,
  contextId,
  createDate = Date.now(),
  isRequestedByRecipient = false,
  defaultCurrencyId = 1,
  approveDate,
  paidAmount = 0,
  fundedAmount = 0,
  quotePrice = 10,
  title,
  description = 'a placeholder description',
  versionInvalidDate,
  parentVersionId,
  status = QuotationStatusApi.PENDING,
  estimatedStartDate = Date.now(),
  estimatedEndDate,
  upfrontFundRequestAmount = 0,
  expiryDate,
  cancelDate,
  rejectDate,
}: GenerateQuotationRevisionOptions): QuotationRevision {
  const endDate: number =
    estimatedEndDate ?? addDays(estimatedStartDate, 1).getTime();

  return {
    id: quotationVersionId ?? generateId(),
    quotationId: quotationId ?? generateId(),
    creatorId,
    contextType,
    contextId: contextId ?? generateId(),
    createDate,
    isRequestedByRecipient,
    defaultCurrencyId,
    approveDate,
    paidAmount: getQuotationAmountObject({
      currencyId: defaultCurrencyId,
      amount: paidAmount,
    }),
    fundedAmount: getQuotationAmountObject({
      currencyId: defaultCurrencyId,
      amount: fundedAmount,
    }),
    quotePrice: getQuotationAmountObject({
      currencyId: defaultCurrencyId,
      amount: quotePrice,
    }),
    title,
    description,
    versionInvalidDate,
    parentVersionId,
    status,
    estimatedStartDate,
    estimatedEndDate: endDate,
    upfrontFundRequestAmount,
    expiryDate: expiryDate ?? addDays(Date.now(), 1).getTime(),
    cancelDate,
    rejectDate,
  };
}

export function generateTimeline({
  estimatedStartDate,
  numDays,
}: {
  readonly estimatedStartDate: number;
  readonly numDays: number;
}): Pick<
  GenerateQuotationRevisionOptions,
  'estimatedStartDate' | 'estimatedEndDate'
> {
  const estimatedEndDate: number = addDays(
    estimatedStartDate,
    numDays,
  ).getTime();
  return {
    estimatedStartDate,
    estimatedEndDate,
  };
}

export function draftQuotation(
  quoteDetails: Pick<
    QuotationRevision,
    'parentVersionId' | 'description' | 'title'
  >,
): Pick<GenerateQuotationRevisionOptions, 'status' | 'parentVersionId'> {
  return { status: QuotationStatusApi.DRAFT, ...quoteDetails };
}

export function currencyUSD(): Pick<
  GenerateQuotationRevisionOptions,
  'defaultCurrencyId'
> {
  return { defaultCurrencyId: 1 };
}

export function approvedQuotation(): Pick<
  GenerateQuotationRevisionOptions,
  'status' | 'approveDate'
> {
  return { status: QuotationStatusApi.APPROVED, approveDate: Date.now() };
}

export function invalidQuotation(): Pick<
  GenerateQuotationRevisionOptions,
  'status' | 'versionInvalidDate'
> {
  return { status: QuotationStatusApi.INVALID, versionInvalidDate: Date.now() };
}

export function rejectedQuotation(): Pick<
  GenerateQuotationRevisionOptions,
  'status' | 'rejectDate'
> {
  return { status: QuotationStatusApi.REJECTED, rejectDate: Date.now() };
}

export function cancelledQuotation(): Pick<
  GenerateQuotationRevisionOptions,
  'status' | 'cancelDate'
> {
  return { status: QuotationStatusApi.CANCELLED, cancelDate: Date.now() };
}

export function expiredQuotation(): Pick<
  GenerateQuotationRevisionOptions,
  'status' | 'expiryDate'
> {
  const pastDate = new Date();
  pastDate.setDate(pastDate.getDate() - 7);
  return { status: QuotationStatusApi.EXPIRED, expiryDate: pastDate.getTime() };
}
